/**
 * The package builder — the quote screen with package buttons
 * (`builder-engine-and-consistency.md` §7, Board 5; phone Board 18 cell 6).
 *
 * A thin page over the engine. `usePackageBuilder` (builder/adapters/package)
 * holds the draft, asks the server's `/preview` for every figure and hands
 * the draft back as the quote's own view shapes, so what is drawn here is the
 * quote's `VisitCard`, `ServiceRow`, `LineRow`, `PlansGrid`, rail cards and
 * popovers — with `Qty rule` where the quote has `Qty` (`RuleCell`), and the
 * package's settings and sample above the visits. Nothing here adds, divides
 * or multiplies money (rule 1).
 */
import { Fragment, type ReactNode } from 'react'
import { packagePlanSentence, type MeResponse, type QuoteTotals, type QuoteVisitView } from '@rowted/shared'
import { cn } from '../lib/cn.js'
import { Band, Button, EmptyState } from '../components/primitives.js'
import { useSettingsBar } from '../settings/settings-bar.js'
import { LineRow } from '../builder/rows/line-row.js'
import { ServiceRow } from '../builder/rows/service-row.js'
import { VisitCard, VisitConnector, followsAbove } from '../builder/rows/visit-card.js'
import { RowMenuButton } from '../builder/rows/add-row.js'
import { RuleCell } from '../builder/quantity/rule-cell.js'
import { UnitInput } from '../builder/quantity/unit-input.js'
import { readNumber } from '../builder/quantity/tiers.js'
import {
  PACKAGE_ADAPTER, indexOf, packagePlanViews, recipeDraftOf, usePackageBuilder, type PackageBuilder,
} from '../builder/adapters/package.js'
import { PlansGrid } from '../quote/plans-grid.js'
import { MarginCard, QuoteTotalCard, RailCard } from '../quote/quote-rail.js'
import { PhoneLineRow, PhoneServiceCard, PhoneVisitCard } from '../quote/quote-phone.js'
import { Toolbar } from '../quote/quote-toolbar.js'
import { PackageSettingsCard } from '../settings/package-settings-card.js'
import { PackageEditorLayer } from '../settings/package-editor-layer.js'
import { SectionLabel } from '../components/primitives.js'
import { SheetOrDialog } from '../components/sheet-or-dialog.js'
import { IconLock } from '../frame/icons.js'
import { IconMoney } from '../builder/rows/glyphs.js'
import { formatDollars, formatNumber } from '../lib/format.js'
import {
  ADD_BILLING_PLAN, AT_THE_SAMPLE_PLAN, BASE_PRICE_AT, BILLING_IN_PACKAGE, DUPLICATE, OFFERED, ON_VISITS,
  PACKAGE_TAG, PRICE_AT_SAMPLE, RULE_RESOLVES, SAVE_CHANGES, SETTINGS_LINK, TRY_IT_WITH, WORKED_OUT_AT,
} from '../builder/copy.js'

export function PackageBuilder({ me }: { me: MeResponse }) {
  const b = usePackageBuilder(me)
  const { draft, tree, phone, canEdit, error } = b

  const bar = useSettingsBar({
    title: draft?.name || 'New package',
    crumbs: ['Catalog', { label: 'Packages', to: '/settings/catalog?tab=packages' }],
    tags: [
      ...(draft?.active ? [{ label: OFFERED, tone: 'ok' as const }] : []),
      { label: PACKAGE_TAG, tone: 'plain' as const },
    ],
    actions: canEdit && draft
      ? (
        phone
          ? (
            <RowMenuButton
              label="Package options"
              onOpen={(at) => b.setMenu({ ...at, items: [{ key: 'duplicate', label: DUPLICATE, onSelect: () => void b.duplicate() }] })}
            />
          )
          : (
            <>
              {b.id !== 'new' && (
                <Button className="h-[34px]" disabled={b.busy} onClick={() => void b.duplicate()}>{DUPLICATE}</Button>
              )}
              <Button variant="primary" className="h-[34px]" disabled={b.busy || !draft.name.trim()}
                onClick={() => void b.save()}>
                {SAVE_CHANGES}
              </Button>
            </>
          )
      )
      : undefined,
  })

  if (error && !draft) {
    return (
      <EmptyState
        title="That package would not open"
        body={error}
        action={{ label: 'Back to the catalog', onClick: () => b.navigate('/settings/catalog') }}
      />
    )
  }
  if (!draft || !tree) return null

  const margin = b.preview?.margin ?? null
  const floor = margin?.byChoice[0]?.options.find((one) => one.inBase)?.name ?? null
  const sampleField = (
    <SampleField b={b} />
  )

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {bar}
      {phone ? <PhoneBody b={b} sampleField={sampleField} floor={floor} /> : (
        <div className={cn('min-h-0 flex-1 overflow-y-auto bg-bg',
          b.railHidden ? 'px-4 pb-24 pt-4 xl:px-6' : 'grid grid-cols-[minmax(0,1fr)_340px] gap-5 px-6 pb-5 pt-4')}>
          <div className="flex min-w-0 flex-col gap-3.5">
            {error && <Band tone="amber">{error}</Band>}
            <PackageSettingsCard draft={draft} types={b.types} units={b.units} canEdit={canEdit} onChange={b.patch} />
            {b.warnings.map((warning) => (
              <Band key={`${warning.visit}-${warning.line}`} tone="amber" title={`${warning.visit} · ${warning.line}`}>
                {warning.message}
              </Band>
            ))}

            <div className="-mx-4 -mt-4 xl:-mx-6">
              <Toolbar
                phone={false}
                canEdit={canEdit}
                searchRef={b.searchBox}
                onSearch={() => { b.anchor.current = b.searchBox.current; b.setEditing({ kind: 'add' }) }}
                packageRef={b.packageBox}
                onAddVisit={() => b.addVisit()}
                onAddPackage={() => { b.anchor.current = b.packageBox.current; b.setEditing({ kind: 'addPackage' }) }}
                railOn={b.railOn}
                onRail={b.narrow ? undefined : () => b.setRailOn((on) => !on)}
                allCollapsed={b.allCollapsed}
                onCollapseAll={b.collapseAll}
                sample={sampleField}
              />
            </div>

            {tree.chooseGroups.map((group) => (
              <PlansGrid
                key={group.id}
                group={group}
                options={tree.chooseOptions}
                choice={margin?.byChoice.find((one) => one.groupId === group.id)}
                targetPct={tree.targetMarginPct}
                visits={tree.visits}
                canEdit={canEdit}
                onReword={(name) => b.patch({ proposalText: name })}
                onReorder={(visitIds) => b.setDraft((was) => (was
                  ? { ...was, visits: visitIds.map((key) => was.visits[indexOf(key)[0]!]!) }
                  : was))}
                onMembership={(visitId, optionIds) => b.setMembership(visitId, optionIds)}
                onAddPlan={(name, recommended) => {
                  const handle = b.addPlan(name)
                  if (recommended) b.recommendPlan(handle, true)
                }}
                onRename={(handle, name) => b.editPlan(handle, { name })}
                onRecommend={(handle, on) => b.recommendPlan(handle, on)}
                onRemovePlan={(handle, keepVisits) => b.removePlan(handle, keepVisits)}
              />
            ))}

            {/* The quote's connector chip between a visit and the one it follows (§10.1). */}
            {tree.visits.map((visit, at) => (
              <Fragment key={visit.id}>
                {followsAbove(visit, tree.visits[at - 1]) && <VisitConnector phrase={visit.phrase.card} />}
                <PackageVisitCard b={b} visit={visit} />
              </Fragment>
            ))}
          </div>

          {!b.railHidden && (
            <aside className="sticky top-0 flex max-h-full w-[340px] shrink-0 flex-col gap-3.5 self-start overflow-y-auto pb-1">
              <Rail b={b} />
            </aside>
          )}
        </div>
      )}
      {/* The iPad's and the narrow window's rail: a bar, and the cards as a sheet (§7.6). */}
      {!phone && b.railHidden && <SampleBar b={b} floor={floor} />}
      {b.summaryOpen && (
        <SheetOrDialog title={PRICE_AT_SAMPLE} onClose={() => b.setSummaryOpen(false)}>
          <div className="flex flex-col gap-3.5"><Rail b={b} /></div>
        </SheetOrDialog>
      )}
      <PackageEditorLayer b={b} />
    </div>
  )
}

/** **Try it with [ 6,000 ] sq ft** — the sample every figure is worked out at (callout ②). */
function SampleField({ b }: { b: PackageBuilder }) {
  const quantity = b.draft?.asks === 'quantity'
  return (
    <span data-try-it="" className="inline-flex h-9 shrink-0 items-center gap-2 rounded-field border border-line-faint bg-surface-alt px-2.5 text-[13px] text-text-muted">
      <span className="text-text-faint"><IconLock size={12} /></span>
      {TRY_IT_WITH}
      <UnitInput
        label={TRY_IT_WITH}
        value={formatNumber(b.sampleValue)}
        onChange={(text) => {
          const value = Math.max(0, readNumber(text))
          b.setSample((was) => (quantity ? { ...was, qty: value } : { ...was, measure: value }))
        }}
        size="small"
        width={84}
        align="right"
      />
      {b.sampleUnit}
    </span>
  )
}

/** One visit: the quote's card, with a Qty rule in each line's quantity cell. */
function PackageVisitCard({ b, visit }: { b: PackageBuilder; visit: QuoteVisitView }) {
  const { tree, draft } = b
  if (!tree || !draft) return null
  const choices = tree.chooseOptions.filter((option) => option.visitIds.includes(visit.id)).map((one) => one.name)
  const [v] = indexOf(visit.id)
  return (
    <div id={`visit-${visit.id}`}>
      <VisitCard
        visit={visit}
        qtyMode={PACKAGE_ADAPTER.qty}
        selected={b.selected === visit.id}
        collapsed={b.collapsed.has(visit.id)}
        targetMarginPct={tree.targetMarginPct}
        onSelect={() => b.setSelected(visit.id)}
        onToggle={() => b.toggle(visit.id)}
        onEditName={b.openVisit(visit.id, 'name')}
        onEditSchedule={b.openVisit(visit.id, 'schedule')}
        onEditBilling={b.openVisit(visit.id, 'billing')}
        onEditNotes={b.openVisit(visit.id, 'notes')}
        onEditProposal={(at) => { b.anchor.current = at; b.setEditing({ kind: 'proposal', visitId: visit.id }) }}
        onQty={(qty) => {
          const timing = draft.visits[v!]!.timing
          if (timing.kind === 'recurring') b.patchVisit(v!, { timing: { ...timing, count: Math.max(1, qty) } })
        }}
        onAddService={(at) => { b.anchor.current = at; b.setEditing({ kind: 'addService', visitId: visit.id }) }}
        onMenu={(at) => b.setMenu({ ...at, items: b.visitMenu(visit) })}
        marginOpen={b.editing?.kind === 'margin' && b.editing.visitId === visit.id}
        onMargin={(at) => b.openMargin(visit.id, at)}
        choices={choices}
        onChoices={b.openVisit(visit.id, 'choices')}
      >
        {visit.services.map((service) => (
          <ServiceRow
            key={service.id}
            service={service}
            collapsed={b.collapsed.has(service.id)}
            onToggle={() => { b.setOpenService(service.id); b.toggle(service.id) }}
            onEditName={b.openServiceEditor(service.id)}
            onEditMeasure={() => document.querySelector<HTMLInputElement>('[data-try-it] input')?.focus()}
            onAdd={(kind, at) => b.openAdd(service.id, kind, at)}
            onMenu={(at) => b.setMenu({ ...at, items: b.serviceMenu(service.id, at.anchor) })}
          >
            {service.lines.map((line) => (
              <LineRow
                key={line.id}
                line={line}
                onQty={() => undefined}
                qtyCell={<LineRule b={b} lineId={line.id} />}
                onOpen={b.openLine(line.id)}
                onMenu={(at) => b.setMenu({ ...at, items: b.lineMenu(line.id, line.priceEdited, at.anchor) })}
              />
            ))}
          </ServiceRow>
        ))}
      </VisitCard>
    </div>
  )
}

/** A line's Qty rule cell, wired to the draft and the preview. */
function LineRule({ b, lineId }: { b: PackageBuilder; lineId: string }) {
  const [v, s, l] = indexOf(lineId)
  const spec = b.draft?.visits[v!]?.services[s!]?.lines[l!]
  const view = b.tree?.visits[v!]?.services[s!]?.lines[l!]
  if (!spec || !view) return null
  // The service pop-out's count: stacked tiers, or the old bands plus the line's own.
  const drawn = recipeDraftOf(spec)
  const tiers = drawn.legacyBands ? drawn.legacyBands.length + 1 : drawn.tiers?.length ?? 0
  return (
    <RuleCell
      label={view.description}
      base={spec.baseQty}
      rate={spec.qtyPerUnit}
      per={b.perOf(spec.scalesBy)}
      resolves={b.preview ? RULE_RESOLVES(formatNumber(view.qty), view.unit, b.sampleText) : null}
      tierCount={tiers}
      onTiers={b.openLine(lineId)}
      onBase={(value) => b.setRule(lineId, value, null)}
      onRate={(value) => b.setRule(lineId, null, value)}
      readOnly={!b.canEdit}
    />
  )
}

/** The rail: Price at the sample, Margin, Billing in this package (§7.1). */
function Rail({ b }: { b: PackageBuilder }) {
  const { tree, preview, draft } = b
  if (!tree || !draft) return null
  const totals = preview
    ? ({
      base: preview.margin.scopes.base.sold,
      options: preview.margin.scopes.options.sold,
      recurring: [],
      margin: preview.margin,
    } as unknown as QuoteTotals)
    : null
  const plans = packagePlanViews(draft)
  return (
    <>
      {totals && (
        <QuoteTotalCard
          title={PRICE_AT_SAMPLE}
          topChoice={false}
          baseLabel={BASE_PRICE_AT(b.sampleText, b.sampleUnit)}
          totals={totals}
          visits={tree.visits}
          onOpenVisit={(visitId) => {
            b.setSelected(visitId)
            document.getElementById(`visit-${visitId}`)?.scrollIntoView({ block: 'center', behavior: 'smooth' })
          }}
        />
      )}
      {preview && (
        <MarginCard
          id="margin-card"
          margin={preview.margin}
          scope={b.marginScope}
          onScope={b.setMarginScope}
          onFixCosts={b.fixCosts}
          hasSubs={b.hasSubs}
          note={WORKED_OUT_AT(b.sampleText, b.sampleUnit)}
        />
      )}
      {/*
        * Billing in this package (§10.2, Board 5 callout 5): each plan — its
        * name, what it does, which visits are on it — opening the 480
        * popover, then + Add a billing plan.
        */}
      <RailCard>
        <SectionLabel className="mb-3 block">{BILLING_IN_PACKAGE}</SectionLabel>
        {plans.map((plan) => {
          const on = tree.visits.filter((visit) => visit.billingPlanId === plan.id)
          return (
            <button key={plan.id} type="button" data-package-plan-row={plan.id}
              onClick={(event) => {
                b.anchor.current = event.currentTarget
                // On a phone the rail is itself a sheet; the plan's sheet takes its place (Board 18).
                if (b.phone) b.setSummaryOpen(false)
                b.setEditing({ kind: 'billingPlan', handle: plan.id })
              }}
              className="flex w-full gap-2.5 border-t border-line-faint py-[9px] text-left">
              <span aria-hidden className="flex h-7 w-7 shrink-0 items-center justify-center rounded-[8px] bg-accent-soft text-accent">
                <IconMoney size={15} />
              </span>
              <span className="flex min-w-0 flex-col gap-0.5">
                <b className="text-[13px] font-bold text-text">{plan.name}</b>
                <span className="text-[12px] leading-[1.4] text-text-muted">{packagePlanSentence(plan)}</span>
                {on.length > 0 && (
                  <span className="text-[11.5px] text-text-faint">{ON_VISITS(on.map((visit) => visit.name).join(', '))}</span>
                )}
              </span>
            </button>
          )
        })}
        {b.canEdit && (
          <button type="button" data-add-billing-plan=""
            onClick={(event) => {
              b.anchor.current = event.currentTarget
              if (b.phone) b.setSummaryOpen(false)
              b.setEditing({ kind: 'billingPlan', handle: b.addBillingPlan() })
            }}
            className={`${plans.length > 0 ? 'pt-2' : ''} text-left text-[12.5px] font-semibold text-accent hover:text-accent-dark`}>
            {ADD_BILLING_PLAN}
          </button>
        )}
      </RailCard>
    </>
  )
}

/** Board 18 cell 6: one column, a sticky price and Save, every choice a sheet. */
function PhoneBody({ b, sampleField, floor }: { b: PackageBuilder; sampleField: ReactNode; floor: string | null }) {
  const { tree, draft, canEdit } = b
  if (!tree || !draft) return null
  const open = tree.visits.find((visit) => visit.id === b.openVisitId)

  return (
    <>
      <div className="min-h-0 flex-1 overflow-y-auto bg-bg px-4 pb-[120px] pt-3">
        {b.error && <Band tone="amber">{b.error}</Band>}
        {open
          ? (
            <div className="flex flex-col gap-2.5">
              <b className="text-[18px] font-bold text-text">{open.name}</b>
              <span className="-mt-2 text-[12.5px] text-text-muted">{open.scheduleLabel}</span>
              {open.services.map((service) => (
                <PhoneServiceCard
                  key={service.id}
                  service={service}
                  canEdit={canEdit}
                  open={b.openService === service.id || open.services.length === 1}
                  onToggle={() => b.setOpenService(b.openService === service.id ? null : service.id)}
                  onEditName={b.openServiceEditor(service.id)}
                  onEditMeasure={() => undefined}
                  onAdd={(kind, at) => b.openAdd(service.id, kind, at)}
                >
                  {service.lines.map((line) => (
                    <PhoneLineRow key={line.id} line={line} onQty={() => undefined}
                      qtyCell={<LineRule b={b} lineId={line.id} />}
                      onOpen={b.openLine(line.id)} targetPct={tree.targetMarginPct} />
                  ))}
                </PhoneServiceCard>
              ))}
            </div>
          )
          : (
            <>
              {canEdit && (
                <div className="mb-3 flex gap-2">
                  <Button size="big" className="flex-1 text-[14px]" onClick={() => b.addVisit()}>Add visit</Button>
                  <span ref={b.packageBox} className="flex flex-1">
                    <Button size="big" className="flex-1 text-[14px]"
                      onClick={() => { b.anchor.current = b.packageBox.current; b.setEditing({ kind: 'addPackage' }) }}>
                      Add package
                    </Button>
                  </span>
                </div>
              )}
              <div className="mb-3 flex items-center gap-2">
                {sampleField}
                <button type="button" onClick={() => b.setEditing({ kind: 'settings' })}
                  className="ml-auto text-[13px] font-semibold text-accent">
                  {SETTINGS_LINK}
                </button>
              </div>
              <div className="flex flex-col gap-3">
                {tree.visits.map((visit) => (
                  <PhoneVisitCard
                    key={visit.id}
                    visit={visit}
                    targetMarginPct={tree.targetMarginPct}
                    onOpen={() => { b.setOpenService(visit.services[0]?.id ?? null); b.openPhoneVisit(visit.id) }}
                    onMenu={(at) => b.setMenu({ ...at, items: b.visitMenu(visit) })}
                    marginOpen={b.editing?.kind === 'margin' && b.editing.visitId === visit.id}
                    onMargin={(at) => b.openMargin(visit.id, at)}
                    choices={tree.chooseOptions.filter((one) => one.visitIds.includes(visit.id)).map((one) => one.name)}
                    onChoices={b.openVisit(visit.id, 'choices')}
                  />
                ))}
              </div>
            </>
          )}
      </div>

      <SampleBar b={b} floor={floor} />
    </>
  )
}

/**
 * The price at the sample and Save changes, pinned to the bottom wherever the
 * rail is not on screen (Board 18 cell 6; the iPad's summary bar). The price
 * opens the rail's cards as a sheet.
 */
function SampleBar({ b, floor }: { b: PackageBuilder; floor: string | null }) {
  const base = b.preview?.margin.scopes.base.sold ?? null
  return (
    <div data-sticky-total="" className="fixed inset-x-0 bottom-0 z-30 flex items-center gap-3 border-t border-line bg-surface px-4 pb-safe pt-3">
      <button type="button" onClick={() => b.setSummaryOpen(true)} className="flex min-w-0 flex-col pb-3 text-left">
        <b className="tnum text-[20px] font-bold text-text">{base === null ? '—' : formatDollars(base)}</b>
        <span className="truncate text-[12px] text-text-muted">{AT_THE_SAMPLE_PLAN(floor)}</span>
      </button>
      {b.canEdit && (
        <Button variant="primary" size="big" className="mb-3 ml-auto px-5"
          disabled={b.busy || !b.draft?.name.trim()} onClick={() => void b.save()}>
          {SAVE_CHANGES}
        </Button>
      )}
    </div>
  )
}
